// Kitchen flow lane D — every English string the purchasing screens show, in
// one place so an Urdu table can be dropped in later. Plain words for staff
// who read little English: short, concrete, no accounting jargon.

export const PURCHASING_STRINGS = {
  par: {
    title: 'How much to keep',
    help: 'When stock falls to “least”, we suggest ordering back up to “most” (twice “least” if “most” is blank).',
    least: 'Least',
    most: 'Most',
    onHand: 'Now',
    save: 'Save',
    saved: 'Saved',
    errNegative: 'Numbers cannot be below zero',
    errMaxBelowMin: '“Most” must be at least “Least”',
    errNaN: 'Type a number',
  },
  prices: {
    title: 'Prices paid',
    none: 'No purchases with a price yet.',
    last5: 'Last 5 buys',
    per: 'per',
    up: 'up',
    down: 'down',
  },
  suggestions: {
    open: 'What to buy',
    title: 'What to buy now',
    help: 'Numbers are filled in for you. Change any, untick what you don’t want, then make the orders.',
    empty: 'Nothing needs buying right now.',
    unsized: (n: number) =>
      `${n === 1 ? '1 item is' : `${n} items are`} out of stock with no “least” / “most” set — set them on the item to get suggestions.`,
    alreadyMade: 'These orders were already made — see Purchase Orders.',
    now: 'Now',
    onOrder: 'Coming',
    buy: 'Buy',
    vendor: 'From',
    noVendor: 'No vendor set',
    byPar: 'Back up to your “most” level',
    byReorder: 'Running low',
    make: (n: number) => (n === 0 ? 'Make orders' : n === 1 ? 'Make 1 order' : `Make ${n} orders`),
    made: (n: number) => (n === 1 ? '1 order made' : `${n} orders made`),
    needsApproval: 'They are waiting for approval.',
    readyToSend: 'They are ready to send.',
    unassigned: 'These have no vendor — set one on the item, then try again:',
  },
  shortfall: {
    button: 'Buy what is short',
    making: 'Making orders…',
    made: (n: number, noVendor: number) =>
      `${n === 1 ? '1 order made' : `${n} orders made`}${noVendor ? ` — ${noVendor} item(s) have no vendor` : ''}`,
    alreadyMade: 'These orders were already made — see Purchase Orders.',
    nothingLeft: 'Nothing left to buy for this request.',
  },
  priceAlerts: {
    open: 'Price rises',
    title: 'Prices that went up',
    help: (pct: number) => `Deliveries that cost ${pct}% or more than the last time.`,
    empty: 'No big price rises in the last 30 days.',
    was: 'was',
    now: 'now',
  },
  pettyCash: {
    open: 'Petty cash',
    title: 'Paid from petty cash',
    help: 'Market buys paid in cash on the spot. No vendor bill is made for these.',
    empty: 'No petty-cash buys in the last 30 days.',
    total: 'Total',
  },
  paidFrom: {
    label: 'How was it paid?',
    credit: 'On credit',
    creditHelp: 'We owe the vendor — a bill is made',
    cash: 'Petty cash',
    cashHelp: 'Paid in cash now — no bill',
  },
  receive: {
    expected: 'Expected',
    received: 'Received',
    whyTitle: 'Why is it different?',
    reasons: ['Short delivery', 'Damaged', 'Wrong item', 'Vendor out of stock', 'Other'] as const,
    otherPlaceholder: 'Say what happened',
    allArrived: 'All arrived',
    lessArrived: (got: string, of: string) => `${got} of ${of} arrived`,
    price: (unit: string) => (unit === 'each' ? 'Price each' : `Price per ${unit}`),
    orderPrice: (price: string) => `Order said ${price}`,
    noOrderPrice: 'No price on the order — type what the bill says',
    priceChanged: 'Price changed',
    noPrice: 'No price on this delivery',
    needsPrice: 'Type the price, or tick “No price on this delivery”',
    badPrice: 'The price can’t be below zero',
    whyPrice: 'It costs more than the order — why?',
    priceReasons: ['Market price went up', 'Different brand or size', 'Vendor added a charge', 'Other'] as const,
    pricePlaceholder: 'Say why the price went up',
  },
  requisition: {
    banner: (no: string | null) => (no ? `For kitchen request ${no}` : 'For a kitchen request'),
  },
  approvers: {
    title: 'Who can approve purchase orders',
    help: 'The admin can always approve. Turn this on to let the Inventory Manager approve too.',
    toggle: 'Inventory Manager can approve',
    selfToggle: 'The person who makes an order may also approve it',
    selfHelp: 'Leave on if one person does the buying. Turn off to need a second person.',
    saved: 'Saved',
    ownOrder: 'You made this order, so someone else has to approve it.',
  },
  returns: {
    credit: 'Credit from supplier',
    creditHelp: (qty: string, unit: string, price: string) => `${qty} ${unit} × ${price} — change it if the vendor agreed another amount.`,
    refund: 'Refund amount',
    inStore: (qty: string, unit: string) => `${qty} ${unit} in the store`,
    inKitchen: (qty: string, unit: string) => `${qty} ${unit} is in the kitchen — send it back to the store first.`,
    overStore: (qty: string, unit: string) => `Only ${qty} ${unit} of this delivery is in the store.`,
  },
  packs: {
    units: (unit: string) => unit || 'units',
    perPack: (label: string) => `per ${label}`,
    equals: (text: string) => `= ${text}`,
  },
  picker: {
    placeholder: 'Search or scan an item',
    none: 'No item matches',
    change: 'Change',
  },
  errors: {
    load: 'Could not load this.',
    retry: 'Retry',
    orders: 'Could not load the orders.',
    suggestions: 'Could not load what to buy.',
    photos: 'Could not load the photos.',
  },
  photos: {
    confirmTitle: 'Remove this photo?',
    confirmBody: 'The delivery slip photo will be deleted.',
    confirm: 'Remove photo',
    cancel: 'Keep it',
    locked: 'This order is closed — only an admin can remove its photos.',
  },
  catalogue: {
    title: 'What this vendor sells',
    help: 'Their pack size and last price fill in new orders for you.',
    add: 'Add item',
    item: 'Item',
    pack: 'Pack',
    packQty: 'Pack size',
    price: 'Price',
    sku: 'Their code',
    preferred: 'Main vendor',
    remove: 'Remove',
    empty: 'No items yet. They are added automatically when you receive from this vendor.',
    save: 'Save',
  },
} as const

export type ReceiveReason = (typeof PURCHASING_STRINGS.receive.reasons)[number]
export type PriceReason = (typeof PURCHASING_STRINGS.receive.priceReasons)[number]
